import { z } from "zod";

/**
 * The terminal protocol is intentionally separate from the primary JSON socket
 * union, because its binary and recovery frames have their own validation
 * boundary. It is the only terminal protocol: every worker and client at the
 * current `PROTOCOL_VERSION` speaks all of it, so nothing is negotiated.
 */
export const TerminalId = z.string().regex(/^term_[0-9abcdefghjkmnpqrstvwxyz]{16}$/);
export type TerminalId = z.infer<typeof TerminalId>;

export const TerminalRuntimeState = z.enum(["creating", "running", "exited", "closed", "failed", "lost"]);
export type TerminalRuntimeState = z.infer<typeof TerminalRuntimeState>;

const TerminalSize = z.object({ cols: z.number().int().min(1).max(500), rows: z.number().int().min(1).max(500) });
const RequestId = z.string().min(1).max(160);
export const TerminalProducerId = z.string().regex(/^prod_[0-9abcdefghjkmnpqrstvwxyz]{16}$/);
/** Running-or-creating terminals one user may hold per organization. Sidebar
 * and workspace-tab terminals draw on the same budget, and a session that was
 * detached (app quit, worktree switch) keeps counting until its unattached
 * retention lapses, so the cap has to leave room for several worktrees' worth
 * of Claude Code, Codex and shell tabs at once. The API enforces it; the
 * clients mirror it so the "+" menu can say why it is disabled. */
export const MAX_TERMINALS_PER_USER = 16;
export const TERMINAL_SNAPSHOT_CHUNK_BYTES = 48 * 1024;
/** One end-to-end ceiling shared by the canonical VT serializer, worker
 * chunker, relay validation, and clients. Keeping this below the replay ring
 * prevents an adversarial screen (for example, millions of combining marks)
 * from creating an unbounded recovery exchange. */
export const TERMINAL_SNAPSHOT_MAX_BYTES = 8 * 1024 * 1024;
export const TERMINAL_SNAPSHOT_MAX_CHUNKS = Math.ceil(
  TERMINAL_SNAPSHOT_MAX_BYTES / TERMINAL_SNAPSHOT_CHUNK_BYTES,
);

export function chunkTerminalSnapshot(data: Uint8Array, chunkBytes = TERMINAL_SNAPSHOT_CHUNK_BYTES): Uint8Array[] {
  if (!Number.isSafeInteger(chunkBytes) || chunkBytes < 1 || chunkBytes > TERMINAL_SNAPSHOT_CHUNK_BYTES) {
    throw new Error("invalid terminal snapshot chunk size");
  }
  if (data.byteLength > TERMINAL_SNAPSHOT_MAX_BYTES) {
    throw new Error("terminal snapshot exceeds protocol maximum");
  }
  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < data.byteLength; offset += chunkBytes) {
    chunks.push(data.slice(offset, Math.min(offset + chunkBytes, data.byteLength)));
  }
  return chunks.length > 0 ? chunks : [new Uint8Array()];
}

export const TerminalCreate = z.object({
  type: z.literal("terminal.create"), requestId: RequestId, terminalId: TerminalId,
  worktreeId: z.string().min(1), ...TerminalSize.shape,
  program: z.enum(["claude", "codex"]).optional(),
});
export const TerminalAttach = z.object({
  type: z.literal("terminal.attach"), requestId: RequestId, terminalId: TerminalId,
  afterSeq: z.number().int().min(-1).default(-1),
  restore: z.enum(["live", "visible", "full"]).default("visible"),
});
export const TerminalDetach = z.object({ type: z.literal("terminal.detach"), requestId: RequestId, terminalId: TerminalId });
export const TerminalClose = z.object({ type: z.literal("terminal.close"), requestId: RequestId, terminalId: TerminalId });
export const TerminalInput = z.object({
  type: z.literal("terminal.input"), terminalId: TerminalId, producerId: TerminalProducerId,
  inputSeq: z.number().int().nonnegative(),
  data: z.string().min(1).max(65_536),
});
export const TerminalResize = z.object({
  type: z.literal("terminal.resize"), terminalId: TerminalId, leaseId: z.string().min(1),
  leaseEpoch: z.number().int().positive(), ...TerminalSize.shape,
});
export const TerminalAck = z.object({
  type: z.literal("terminal.ack"), terminalId: TerminalId, incarnation: z.number().int().positive(),
  appliedSeq: z.number().int().min(-1),
  creditBytes: z.number().int().min(0).max(16 * 1024 * 1024),
});

export const TerminalControlFrame = z.discriminatedUnion("type", [
  TerminalCreate, TerminalAttach, TerminalDetach, TerminalClose, TerminalInput, TerminalResize, TerminalAck,
]);
export type TerminalControlFrame = z.infer<typeof TerminalControlFrame>;

/** Binary payload frames are deliberately self-routing: a relay does not need a
 * connection-local slot table to determine the stable terminal identity. */
export const TERMINAL_BINARY_MAGIC = 0x54;
export const TERMINAL_BINARY_VERSION = 1;
export const TerminalBinaryOpcode = { output: 1, input: 2, snapshot: 3, resize: 4 } as const;
export type TerminalBinaryOpcode = (typeof TerminalBinaryOpcode)[keyof typeof TerminalBinaryOpcode];

export interface TerminalBinaryFrame {
  opcode: TerminalBinaryOpcode;
  terminalId: string;
  incarnation: number;
  sequence: bigint;
  payload: Uint8Array;
}

const OPCODES = new Set<number>(Object.values(TerminalBinaryOpcode));

export function encodeTerminalBinaryFrame(frame: TerminalBinaryFrame): Uint8Array {
  const id = new TextEncoder().encode(frame.terminalId);
  if (id.byteLength === 0 || id.byteLength > 255) throw new Error("terminal id does not fit binary frame");
  if (!Number.isInteger(frame.incarnation) || frame.incarnation < 0 || frame.incarnation > 0xffff_ffff) {
    throw new Error("invalid terminal incarnation");
  }
  if (frame.sequence < 0n || frame.sequence > 0xffff_ffff_ffff_ffffn) throw new Error("invalid terminal sequence");
  const out = new Uint8Array(16 + id.byteLength + frame.payload.byteLength);
  const view = new DataView(out.buffer);
  out[0] = TERMINAL_BINARY_MAGIC;
  out[1] = TERMINAL_BINARY_VERSION;
  out[2] = frame.opcode;
  out[3] = id.byteLength;
  view.setUint32(4, frame.incarnation);
  view.setBigUint64(8, frame.sequence);
  out.set(id, 16);
  out.set(frame.payload, 16 + id.byteLength);
  return out;
}

export function decodeTerminalBinaryFrame(value: Uint8Array | ArrayBuffer): TerminalBinaryFrame | undefined {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  if (bytes.byteLength < 17 || bytes[0] !== TERMINAL_BINARY_MAGIC || bytes[1] !== TERMINAL_BINARY_VERSION) return undefined;
  const opcode = bytes[2]!;
  const idLength = bytes[3] ?? 0;
  if (!OPCODES.has(opcode) || idLength === 0 || bytes.byteLength < 16 + idLength) return undefined;
  const terminalId = new TextDecoder().decode(bytes.subarray(16, 16 + idLength));
  if (!TerminalId.safeParse(terminalId).success) return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    opcode: opcode as TerminalBinaryOpcode,
    terminalId,
    incarnation: view.getUint32(4),
    sequence: view.getBigUint64(8),
    payload: bytes.slice(16 + idLength),
  };
}
